/*
 * Modelo de navegación del shell (rediseño jul-2026).
 *
 * FUSIÓN: parte de los grupos del diseño (redesign/PaySlip App.dc.html) pero
 * incorpora TODO lo que hoy es alcanzable en la app, para no perder accesos:
 *   - "Prospectos" (hoy en el menú de usuario) → grupo Plataforma.
 *   - "Portal de empleados" del rol empresa (hoy es una pestaña) → Mi empresa.
 *   - "Mi plan" y "Facturación" del rol empresa apuntan a sus rutas reales
 *     (/app/account/plan y /app/account/billing), no a las vistas de super admin
 *     que el prototipo reutilizaba para simular ambos roles.
 * Los accesos legales (Términos/Privacidad) y "Cerrar sesión" viven en el menú
 * de usuario del header, igual que hoy.
 *
 * Rutas alcanzables desde otras pantallas y que por eso NO están en el menú
 * (igual que en la app actual): /app/company-form (desde Empresas),
 * /app/company-administrator-management (desde la ficha de una empresa) y
 * /app/account/plans/upgrade (desde Mi plan).
 */

export const ROLE_LABEL = {
  administrator: 'Super Administrador',
  company: 'Empresa',
  employee: 'Colaborador',
};

export const ROLE_ICON = {
  administrator: 'shield_person',
  company: 'domain',
  employee: 'badge',
};

/** Ruta de inicio de cada rol (el /app "pelón" redirige aquí). */
export const DEFAULT_ROUTE = {
  administrator: '/app/system-analytics',
  company: '/app/company-dashboard',
  employee: '/app/employee-payroll-portal',
};

export function defaultRouteFor(role) {
  return DEFAULT_ROUTE[role] || '/app/account';
}

/** Grupos del menú lateral por rol. */
export function navGroupsFor(role) {
  if (role === 'administrator') {
    return [
      {
        label: 'Plataforma',
        items: [
          { to: '/app/system-analytics', icon: 'insights', label: 'Analíticas' },
          { to: '/app/company-management', icon: 'domain', label: 'Empresas' },
          { to: '/app/system-user-management', icon: 'shield_person', label: 'Super Admins' },
          { to: '/app/admin/prospects', icon: 'chat', label: 'Prospectos' },
        ],
      },
      {
        label: 'Facturación',
        items: [
          { to: '/app/admin/subscriptions', icon: 'credit_card', label: 'Suscripciones' },
          { to: '/app/admin/coupons', icon: 'sell', label: 'Cupones' },
        ],
      },
      {
        label: 'Operación',
        items: [
          { to: '/app/payroll-processing', icon: 'sync', label: 'Sincronización SAT' },
          { to: '/app/sync', icon: 'history', label: 'Sincronizaciones' },
          { to: '/app/company-dashboard', icon: 'dashboard', label: 'Panel de empresa' },
          { to: '/app/receipts', icon: 'receipt_long', label: 'Recibos' },
        ],
      },
    ];
  }

  if (role === 'company') {
    return [
      {
        label: 'Mi empresa',
        items: [
          { to: '/app/company-dashboard', icon: 'dashboard', label: 'Panel' },
          { to: '/app/payroll-processing', icon: 'sync', label: 'Sincronización SAT' },
          { to: '/app/sync', icon: 'history', label: 'Sincronizaciones' },
          { to: '/app/receipts', icon: 'receipt_long', label: 'Recibos' },
        ],
      },
      {
        label: 'Suscripción',
        items: [
          { to: '/app/account/plan', icon: 'workspace_premium', label: 'Mi plan' },
          { to: '/app/account/billing', icon: 'receipt_long', label: 'Facturación' },
        ],
      },
    ];
  }

  return [
    {
      label: 'Autoservicio',
      items: [
        { to: '/app/employee-payroll-portal', icon: 'receipt_long', label: 'Mis recibos' },
      ],
    },
  ];
}

/** Barra inferior en móvil (los accesos más frecuentes de cada rol). */
export function mobileNavFor(role) {
  if (role === 'administrator') {
    return [
      { to: '/app/system-analytics', icon: 'insights', label: 'Analíticas' },
      { to: '/app/company-management', icon: 'domain', label: 'Empresas' },
      { to: '/app/payroll-processing', icon: 'sync', label: 'SAT' },
      { to: '/app/admin/subscriptions', icon: 'credit_card', label: 'Planes' },
      { to: '/app/account', icon: 'person', label: 'Cuenta' },
    ];
  }
  if (role === 'company') {
    return [
      { to: '/app/company-dashboard', icon: 'dashboard', label: 'Panel' },
      { to: '/app/payroll-processing', icon: 'sync', label: 'SAT' },
      { to: '/app/account/plan', icon: 'workspace_premium', label: 'Plan' },
      { to: '/app/account', icon: 'person', label: 'Cuenta' },
    ];
  }
  return [
    { to: '/app/employee-payroll-portal', icon: 'receipt_long', label: 'Recibos' },
    { to: '/app/account', icon: 'person', label: 'Cuenta' },
  ];
}

/*
 * Título de cada pantalla: [kicker, título]. El header los pinta.
 * Las pantallas con título dinámico (ficha de prospecto, edición de empresa)
 * lo sobrescriben con el hook usePageTitle.
 */
const PAGE_META = {
  '/app/system-analytics': ['Super Administrador', 'Análisis del sistema'],
  '/app/administrator-dashboard': ['Super Administrador', 'Panel de control'],
  '/app/company-management': ['Plataforma', 'Gestión de empresas'],
  '/app/company-form': ['Plataforma', 'Empresa'],
  '/app/company-administrator-management': ['Plataforma', 'Administradores de empresa'],
  '/app/system-user-management': ['Plataforma', 'Super Administradores'],
  '/app/admin/prospects': ['Plataforma', 'Prospectos'],
  '/app/admin/subscriptions': ['Facturación', 'Suscripciones'],
  '/app/admin/coupons': ['Facturación', 'Cupones'],
  '/app/payroll-processing': ['Operación', 'Sincronización SAT'],
  '/app/employee-payroll-portal': ['Autoservicio', 'Portal del empleado'],
  '/app/receipts': ['Operación', 'Recibos'],
  '/app/sync': ['Operación', 'Sincronizaciones'],
  '/app/account': ['Cuenta', 'Mi cuenta'],
  '/app/account/profile': ['Cuenta', 'Mi cuenta'],
  '/app/user-profile-management': ['Cuenta', 'Perfil de usuario'],
  '/app/account/plan': ['Suscripción', 'Mi plan'],
  '/app/account/plans/upgrade': ['Suscripción', 'Actualizar plan'],
  '/app/account/billing': ['Suscripción', 'Facturación'],
};

/**
 * Deriva [kicker, título] de la ruta. El panel de empresa cambia de kicker según
 * quién lo ve: para el propio administrador de empresa es "Mi empresa"; para el
 * super admin que lo inspecciona es "Operación".
 */
export function pageMetaFor(pathname, role) {
  if (pathname.startsWith('/app/admin/prospects/')) return ['Plataforma', 'Ficha de prospecto'];
  if (pathname === '/app/company-dashboard') {
    return role === 'company' ? ['Mi empresa', 'Panel'] : ['Operación', 'Panel de empresa'];
  }
  return PAGE_META[pathname] || ['', 'PaySlip'];
}

/** Iniciales para el avatar (máximo 2 letras). */
export function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
